import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../context/AuthContext';
import { authService, uploadService } from '../services/api';

const ProfileSchema = Yup.object().shape({
  name: Yup.string()
    .required('El nombre es obligatorio')
    .min(3, 'El nombre debe tener al menos 3 caracteres'),
  bio: Yup.string()
    .max(500, 'La biografía no puede tener más de 500 caracteres'),
  walletAddress: Yup.string()
    .nullable()
    .matches(/^(0x)?[0-9a-fA-F]{40}$/, 'Dirección de wallet inválida')
    .transform((value) => value === '' ? null : value),
  notifications: Yup.boolean()
});

const Profile = () => {
  const { currentUser, loadUserProfile } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const response = await authService.getProfile();
        setProfileData(response.data);
        setError(null);
      } catch (err) {
        console.error('Error al cargar perfil:', err);
        setError('No se pudo cargar la información del perfil');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const response = await authService.updateProfile(values);
      setProfileData(response.data.user);
      setSuccess('Perfil actualizado correctamente');
      setError(null);
      
      // Actualizar el contexto de autenticación
      loadUserProfile();

      // Limpiar mensaje de éxito después de 3 segundos
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error al actualizar perfil:', err);
      setError(err.response?.data?.message || 'Error al actualizar el perfil');
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const response = await uploadService.uploadImage(file);
      
      // Actualizar el perfil con la nueva imagen
      await authService.updateProfile({
        profilePicture: response.data.imageUrl
      });
      
      // Actualizar la información del perfil
      const updatedProfile = await authService.getProfile();
      setProfileData(updatedProfile.data);
      
      // Actualizar el contexto de autenticación
      loadUserProfile();
      
      setSuccess('Imagen de perfil actualizada correctamente');
      
      // Limpiar mensaje de éxito después de 3 segundos
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error al subir imagen:', err);
      setError('Error al subir la imagen de perfil');
    } finally {
      setUploadingImage(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="mt-2">Cargando información del perfil...</p>
      </div>
    );
  }

  if (!profileData && !loading) {
    return (
      <div className="alert alert-warning my-5" role="alert">
        No se pudo cargar la información del perfil. Por favor, intenta de nuevo más tarde.
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-lg-8 mx-auto">
          <div className="card shadow">
            <div className="card-header bg-primary text-white">
              <h2 className="h4 mb-0">Mi Perfil</h2>
            </div>
            <div className="card-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}
              
              {success && (
                <div className="alert alert-success" role="alert">
                  {success}
                </div>
              )}
              
              <div className="text-center mb-4">
                <div className="position-relative d-inline-block">
                  <img 
                    src={profileData.profilePicture || 'https://via.placeholder.com/150'} 
                    alt="Foto de perfil" 
                    className="rounded-circle"
                    style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                  />
                  <label 
                    htmlFor="profile-image" 
                    className="btn btn-sm btn-primary position-absolute bottom-0 end-0"
                    style={{ borderRadius: '50%' }}
                  >
                    <i className="fas fa-camera"></i>
                  </label>
                  <input 
                    type="file" 
                    id="profile-image" 
                    className="d-none" 
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                  />
                </div>
                
                {uploadingImage && (
                  <div className="mt-2">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Subiendo...</span>
                    </div>
                    <span className="ms-2">Subiendo imagen...</span>
                  </div>
                )}
              </div>
              
              <div className="row mb-3">
                <div className="col-md-6">
                  <div className="info-item">
                    <span className="text-muted">Email:</span>
                    <span className="fw-bold ms-2">{profileData.email}</span>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="info-item">
                    <span className="text-muted">Miembro desde:</span>
                    <span className="fw-bold ms-2">
                      {new Date(profileData.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="row mb-4">
                <div className="col-6">
                  <div className="card bg-light">
                    <div className="card-body text-center">
                      <h5 className="text-primary">{profileData.tokensBlue || 0}</h5>
                      <p className="mb-0">BLUE Tokens</p>
                    </div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="card bg-light">
                    <div className="card-body text-center">
                      <h5 className="text-danger">{profileData.tokensRed || 0}</h5>
                      <p className="mb-0">RED Tokens</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <Formik
                initialValues={{
                  name: profileData.name || '',
                  bio: profileData.bio || '',
                  walletAddress: profileData.walletAddress || '',
                  notifications: profileData.notifications !== false
                }}
                validationSchema={ProfileSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, errors, touched }) => (
                  <Form>
                    <div className="mb-3">
                      <label htmlFor="name" className="form-label">Nombre</label>
                      <Field
                        type="text"
                        id="name"
                        name="name"
                        className={`form-control ${errors.name && touched.name ? 'is-invalid' : ''}`}
                      />
                      <ErrorMessage name="name" component="div" className="invalid-feedback" />
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor="bio" className="form-label">Biografía</label>
                      <Field
                        as="textarea"
                        id="bio"
                        name="bio"
                        rows="4"
                        className={`form-control ${errors.bio && touched.bio ? 'is-invalid' : ''}`}
                        placeholder="Cuéntanos sobre ti..."
                      />
                      <ErrorMessage name="bio" component="div" className="invalid-feedback" />
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor="walletAddress" className="form-label">
                        <i className="fas fa-wallet me-2"></i>
                        Dirección de Wallet (Ethereum)
                      </label>
                      <div className="input-group">
                        <Field
                          type="text"
                          id="walletAddress"
                          name="walletAddress"
                          className={`form-control ${errors.walletAddress && touched.walletAddress ? 'is-invalid' : ''}`}
                          placeholder="0x..."
                        />
                        <button 
                          type="button" 
                          className="btn btn-outline-secondary"
                          onClick={() => {
                            // Aquí iría la lógica para conectar con MetaMask u otro proveedor
                            alert('Funcionalidad de conexión con MetaMask en desarrollo');
                          }}
                        >
                          <i className="fas fa-plug me-1"></i> Conectar
                        </button>
                      </div>
                      <ErrorMessage name="walletAddress" component="div" className="invalid-feedback" />
                      <div className="form-text">
                        Necesaria para recibir pagos con tokens BLUE y RED. Debe ser compatible con Ethereum (ERC20).
                      </div>
                    </div>
                    
                    <div className="mb-3 form-check">
                      <Field type="checkbox" name="notifications" className="form-check-input" id="notifications" />
                      <label className="form-check-label" htmlFor="notifications">
                        Recibir notificaciones
                      </label>
                    </div>
                    
                    <div className="d-grid mt-4">
                      <button 
                        type="submit" 
                        className="btn btn-primary"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Guardando...
                          </>
                        ) : 'Guardar Cambios'}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
